import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, categories } from '@/mocks/data';

const categoryImages: Record<string, string> = {
  tutorial: 'https://readdy.ai/api/search-image?query=Clean%20modern%20tutorial%20and%20learning%20concept%20with%20open%20books%20and%20digital%20screens%2C%20warm%20amber%20tones%2C%20organized%20workspace%20with%20study%20materials%2C%20soft%20minimal%20aesthetic&width=1600&height=400&seq=category-banner-tutorial&orientation=landscape',
  faq: 'https://readdy.ai/api/search-image?query=Help%20center%20and%20customer%20support%20concept%20with%20floating%20question%20marks%2C%20warm%20professional%20atmosphere%2C%20clean%20organized%20layout%2C%20soft%20lighting%2C%20modern%20minimal&width=1600&height=400&seq=category-banner-faq&orientation=landscape',
  security: 'https://readdy.ai/api/search-image?query=Cyber%20security%20and%20data%20protection%20concept%20with%20shield%20icons%2C%20warm%20professional%20tones%2C%20abstract%20security%20layers%2C%20clean%20modern%20design&width=1600&height=400&seq=category-banner-security&orientation=landscape',
  news: 'https://readdy.ai/api/search-image?query=News%20and%20announcements%20concept%20with%20digital%20updates%2C%20warm%20modern%20aesthetic%2C%20clean%20organized%20layout%2C%20abstract%20notification%20visuals&width=1600&height=400&seq=category-banner-news&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const category = categories.find((c) => c.slug === slug);
  const [activeTab, setActiveTab] = useState(slug || 'tutorial');

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  const categoryArticles = articles.filter((a) => a.category === activeTab);
  const currentCategory = categories.find((c) => c.slug === activeTab);

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden">
          <img
            src={categoryImages[activeTab] || categoryImages.tutorial}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-3xl font-bold text-background-50 mb-3">
              {currentCategory?.name || '分类'}
            </h1>
            <p className="text-background-50/80 text-sm md:text-base">
              {currentCategory?.description || ''}
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <Breadcrumb items={[{ label: '首页', path: '/' }, { label: currentCategory?.name || '' }]} />
            </div>

            <div className="flex items-center gap-2 mb-10 p-1 bg-background-100 rounded-full w-fit">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-foreground-400 text-xs">{article.date}</span>
                      <span className="text-foreground-400 text-xs">&middot;</span>
                      <span className="text-foreground-400 text-xs">{article.readTime}</span>
                    </div>
                    <h3 className="font-semibold text-foreground-950 mb-2 text-base line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2 mb-4">{article.excerpt}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}